import { useEffect, type MutableRefObject, type RefObject } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useQueryEditorMcpStore } from '../store/queryEditorMcpStore';
import { toast } from '../../../../components/ui/toaster';
import { decidePlanCommand } from './planCommandDispatch';
import { readLiveQuery } from '../components/liveQuery';
import type { QueryTab } from '../../core/types/workspaceTypes';
import type { QueryEditorHandle } from '../components/QueryEditor';

type UseTabMcpCommandsParams = {
  tabId: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  chartConfigs: NonNullable<QueryTab['content']>['chartConfigs'];
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  handleExplainPlan: () => Promise<void>;
  handleExecuteWithActualPlan: () => void;
};

/**
 * MCP command consumption for a query editor tab (ui_control tools):
 * switch_tab / execute_with_plan / show_chart / set_chart_config.
 * select_row and show_row_details are handled by ResultsGrid. Behavior
 * carried verbatim from the previous inline implementation.
 */
export function useTabMcpCommands({
  tabId,
  activeTab,
  setActiveTab,
  chartConfigs,
  editorRef,
  queryRef,
  handleExplainPlan,
  handleExecuteWithActualPlan,
}: UseTabMcpCommandsParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  const getPendingMcpCommands = useQueryEditorMcpStore((state) => state.getPendingCommands);
  const completeMcpCommand = useQueryEditorMcpStore((state) => state.completeCommand);
  const mcpPendingCommandsLength = useQueryEditorMcpStore((state) =>
    state.pendingCommands.filter((cmd) => cmd.tabId === tabId).length
  );

  useEffect(() => {
    const commands = getPendingMcpCommands(tabId);
    console.log(`[QueryEditorTab MCP] Processing ${commands.length} commands for tab ${tabId}, mcpPendingCommandsLength=${mcpPendingCommandsLength}`);
    for (const pending of commands) {
      const cmd = pending.command;
      console.log(`[QueryEditorTab MCP] Processing command: ${cmd.type}`, cmd);
      switch (cmd.type) {
        case 'switch_tab':
          setActiveTab(cmd.resultTab);
          break;
        case 'execute_with_plan': {
          // The binding check lives in `decidePlanCommand` so a test can drive
          // it — inline here, deleting the comparison left the whole suite
          // green. The buffer is read right here, before the handlers below run
          // any formatting pass over it.
          const planBuffer = readLiveQuery(editorRef.current, queryRef.current, 'Execution cancelled');
          if (planBuffer === null) break; // refused (toast shown); still complete the command below
          const decision = decidePlanCommand(cmd, planBuffer);
          if (decision.kind === 'refuse') {
            toast.error({ title: 'Execution cancelled', description: decision.message });
          } else if (decision.kind === 'explain') {
            void (async () => {
              await handleExplainPlan();
            })();
          } else {
            void (async () => {
              await handleExecuteWithActualPlan();
            })();
          }
          break;
        }
        case 'show_chart':
          console.log(`[QueryEditorTab MCP] show_chart: show=${cmd.show}, currentActiveTab=${activeTab}`);
          if (cmd.show) {
            setActiveTab('chart');
          } else if (activeTab === 'chart') {
            setActiveTab('results');
          }
          break;
        case 'set_chart_config': {
          const existingConfigs = chartConfigs || {};
          const resultSetIdx = cmd.resultSetIndex ?? 0;
          const newConfigs = { ...existingConfigs, [resultSetIdx]: cmd.config };
          console.log(`[QueryEditorTab MCP] set_chart_config: updating chartConfigs and switching to chart tab`);
          updateTabContent(tabId, { chartConfigs: newConfigs });
          setActiveTab('chart');
          break;
        }
        // select_row and show_row_details are handled by ResultsGrid
        default:
          break;
      }
      completeMcpCommand(pending.id);
    }
  }, [tabId, mcpPendingCommandsLength, getPendingMcpCommands, completeMcpCommand, chartConfigs, updateTabContent, activeTab, editorRef, queryRef, handleExplainPlan, handleExecuteWithActualPlan]);
}
